// Must be first: exposes window.DeeplySmart before any plugin bundle can load.
import "./plugins/hostGlobals";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { POSTHOG_KEY, captureException } from "./lib/posthog";
import App from "./App";
import "./index.css";
import 'highlight.js/styles/github-dark.css';

// Initialize PostHog when key is present (env or fallback in lib/posthog)
if (POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
  });
}

// Initialize Sentry before any other app code.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN ?? "https://48133dc4c6680fb96a68cf17f62fe9f8@o4510840537612288.ingest.us.sentry.io/4510840555110400";
Sentry.init({
  dsn: sentryDsn,
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.vercel\.app\/api/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  sendDefaultPii: false,
});

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Loading chunk") ||
    message.includes("Importing a module script failed")
  );
}

const container = document.getElementById("root")!;
const root = createRoot(container);
const app = (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => {
      if (error) captureException(error, { section: "root" });
      const chunkError = error != null && isChunkLoadError(error);
      return (
        <div style={{ padding: "2rem", textAlign: "center", fontFamily: "system-ui", maxWidth: "32rem", margin: "0 auto" }}>
          <h2>Something went wrong</h2>
          <p style={{ marginTop: "0.5rem", color: "#64748b" }}>
            {chunkError
              ? "The app couldn’t load part of the page—this often happens after an update. Refreshing usually fixes it."
              : "An unexpected error occurred."}
          </p>
          {!chunkError && error?.message && (
            <pre style={{ textAlign: "left", overflow: "auto", maxWidth: "100%", fontSize: "0.875rem", marginTop: "1rem" }}>
              {error.message}
            </pre>
          )}
          <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{ padding: "0.5rem 1rem", cursor: "pointer" }}
            >
              {chunkError ? "Refresh page" : "Refresh page"}
            </button>
            {!chunkError && (
              <button type="button" onClick={resetError} style={{ padding: "0.5rem 1rem", cursor: "pointer" }}>
                Try again
              </button>
            )}
          </div>
        </div>
      );
    }}
  >
    <App />
  </Sentry.ErrorBoundary>
);

root.render(
  POSTHOG_KEY ? (
    <PostHogProvider client={posthog}>
      {app}
    </PostHogProvider>
  ) : (
    app
  )
);
